import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { useToastNotification } from "../packages/WPToastNotification";

import { DEFAULT_LICENSE_PAYLOAD } from "../constants/LicenseManagement.constants";

import {
  getLicensePermission,
  getUserLicenseData,
} from "../network/LicenseApis";

import { storeLicensePermissions } from "../Store/TemplateStore/template.action";

const useLicenseManagementData = (initialPayload = DEFAULT_LICENSE_PAYLOAD) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { showErrorNotification } = useToastNotification();
  const rawLicensePermissions = useSelector(
    (state) => state.licensePermissions,
  );
  const licensePermissions = Array.isArray(rawLicensePermissions)
    ? rawLicensePermissions
    : [];

  const [licensePayload, setLicensePayload] = useState(initialPayload);
  const [userRoleLicenses, setUserRoleLicenses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchLicenseManagementData = async (
    requestPayload,
    isInitialLoad = false,
    isPaginationChange = false,
  ) => {
    if (!requestPayload) return;

    try {
      setIsLoading(true);
      if (isInitialLoad) {
        const [userLicenseResponse, licensePermissionResponse] =
          await Promise.all([
            getUserLicenseData(requestPayload),
            getLicensePermission(),
          ]);
        setUserRoleLicenses(userLicenseResponse ?? []);
        dispatch(storeLicensePermissions(licensePermissionResponse.data ?? []));
      } else {
        const userRoleLicensesData = await getUserLicenseData(requestPayload);
        setUserRoleLicenses(userRoleLicensesData ?? []);
      }
    } catch (err) {
      showErrorNotification(t("notificationStrings.fetchFail"));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * This effect runs only on initial mount
   * to load initial data (calls both user role and license permission APIs)
   */
  useEffect(() => {
    fetchLicenseManagementData(licensePayload, true);
  }, []);

  /** This effect is triggered when licensePayload changes (e.g., filters, search, pagination) */
  useEffect(() => {
    if (licensePayload !== initialPayload) {
      const isPaginationChange = ["pageNumber", "pageSize"].some(
        (key) => licensePayload[key] !== initialPayload[key],
      );
      fetchLicenseManagementData(licensePayload, false, isPaginationChange);
    }
  }, [licensePayload]);

  /**
   * Updates the licensePayload state with new filters/search/pagination params
   * Triggers data reload via useEffect
   */
  const updateLicensePayload = useCallback((newParams) => {
    setLicensePayload((prev) => ({ ...prev, ...newParams }));
  }, []);

  return {
    userRoleLicenseData: userRoleLicenses,
    licensePermissions,
    licensePayload,
    updateLicensePayload,
    isLoading,
  };
};

export default useLicenseManagementData;
